/**
 * Batch #14.26 — PortalPaymentSuccess.jsx
 *
 * Page de retour Stripe après un paiement client (facture ou enquête de crédit).
 * Affiche un message clair de succès + un bouton pour revenir au portail.
 *
 * Routes possibles selon le `type` :
 *  - ?type=invoice&invoice_id=...&session_id=... → paiement de facture
 *  - ?type=credit_check&session_id=...           → enquête de crédit
 *  - ?cancelled=1                                → paiement annulé
 *
 * Le backend est la source de vérité (webhook Stripe) — cette page se contente
 * de rassurer le client visuellement et de pointer vers son portail.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, ArrowRight, XCircle, Home, Loader2 } from "lucide-react";

export default function PortalPaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const cancelled = params.get("cancelled") === "1";
  const type = params.get("type") || "invoice";
  const sessionId = params.get("session_id");

  // Auto-redirect au dashboard après X secondes (UX touche finale)
  const AUTO_REDIRECT_SECS = 8;
  const [countdown, setCountdown] = useState(AUTO_REDIRECT_SECS);

  useEffect(() => {
    if (cancelled) return; // Pas d'auto-redirect en cas d'annulation
    if (countdown <= 0) {
      navigate("/portal/dashboard", { replace: true });
      return;
    }
    const t = setTimeout(() => setCountdown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, cancelled, navigate]);

  const labels = useMemo(() => {
    if (cancelled) {
      return {
        title: "Paiement annulé",
        subtitle: "Aucun montant n'a été prélevé. Vous pouvez réessayer à tout moment depuis votre portail.",
        toneFrom: "from-amber-500",
        toneTo: "to-orange-600",
        bgFrom: "from-amber-50",
        bgVia: "via-white",
        bgTo: "to-orange-50",
        icon: XCircle,
      };
    }
    return {
      title: "Merci ! Votre paiement a été traité avec succès.",
      subtitle:
        type === "credit_check"
          ? "Votre enquête de crédit est maintenant en cours de traitement."
          : "Votre facture est désormais marquée comme payée dans votre portail.",
      toneFrom: "from-emerald-500",
      toneTo: "to-teal-600",
      bgFrom: "from-emerald-50",
      bgVia: "via-white",
      bgTo: "to-blue-50",
      icon: CheckCircle2,
    };
  }, [cancelled, type]);

  const Icon = labels.icon;

  return (
    <div
      className={`min-h-screen bg-gradient-to-br ${labels.bgFrom} ${labels.bgVia} ${labels.bgTo} flex items-center justify-center px-4 py-12`}
      data-testid="portal-payment-success-page"
    >
      <div className="max-w-lg w-full">
        {/* Card principale */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200">
          {/* Header tonal */}
          <div className={`px-8 py-12 text-center bg-gradient-to-br ${labels.toneFrom} ${labels.toneTo}`}>
            <div className="w-24 h-24 mx-auto rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-5 ring-8 ring-white/10">
              <Icon className="w-14 h-14 text-white" strokeWidth={2.5} data-testid="portal-payment-icon" />
            </div>
            <h1 className="text-2xl font-bold text-white leading-tight" data-testid="portal-payment-title">
              {labels.title}
            </h1>
          </div>

          {/* Corps */}
          <div className="px-8 py-8 space-y-5">
            <p className="text-slate-600 text-center text-[15px] leading-relaxed" data-testid="portal-payment-subtitle">
              {labels.subtitle}
            </p>

            {!cancelled && (
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-sm">
                <div className="text-emerald-800 text-[13px] leading-relaxed space-y-1.5">
                  <div className="font-semibold mb-1">Que se passe-t-il maintenant ?</div>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Un reçu Stripe vous a été envoyé par courriel.</li>
                    <li>Vous pouvez consulter le statut à tout moment depuis votre portail.</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Bouton retour au portail */}
            <button
              onClick={() => navigate("/portal/dashboard", { replace: true })}
              className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-white text-[15px] shadow-md hover:shadow-lg transition-all bg-gradient-to-r ${labels.toneFrom} ${labels.toneTo} hover:brightness-110`}
              data-testid="portal-payment-back-to-dashboard-btn"
            >
              <Home className="w-5 h-5" />
              Retour au portail
              <ArrowRight className="w-4 h-4" />
            </button>

            {!cancelled && countdown > 0 && (
              <div
                className="text-center text-[12px] text-slate-400 flex items-center justify-center gap-1.5"
                data-testid="portal-payment-countdown"
              >
                <Loader2 className="w-3 h-3 animate-spin" />
                Redirection automatique dans {countdown} seconde{countdown > 1 ? "s" : ""}…
              </div>
            )}

            {sessionId && (
              <div className="text-[10px] text-slate-300 font-mono break-all bg-slate-50 px-3 py-2 rounded-lg text-center">
                Référence Stripe : {sessionId}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-8 py-4 bg-slate-50 border-t border-slate-100 text-center">
            <p className="text-[11px] text-slate-400">
              Sécurisé par Stripe · Géré par DG Comptabilité
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
